import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  isEmptyCollection,
  useApplicationState,
  useCasinoNewState,
  useCategoryGamesState,
} from '@atlas/core';
import { Loader, ViewportDisplayController } from '@atlas/ui';

import { EmptyState } from 'components/Casino/components/EmptyState';
import { GAME_SIZES, GROUPS } from 'components/Casino/constants';
import { casinoFilterTypeMap, deleteCasinoFilter } from 'components/Casino/useCasinoFilter';
import history from 'components/Layout/history';
import { useCasinoFilterState } from 'src/components/Casino/CasinoFilterContext';

import { CasinoCategory } from './CasinoCategory';
import { scrollToTopHandler } from 'src/components/Footer/scrollToTopHandler';
import PropTypes from 'prop-types';

const casinoSliderSettings = [
  { rows: 2, size: GAME_SIZES.MEDIUM },
  { rows: 1, size: GAME_SIZES.LARGE },
];

const defaultCasinoSliderSettings = { rows: 1, size: GAME_SIZES.SMALL };

const liveCasinoSliderSettings = { rows: 1, size: GAME_SIZES.MEDIUM };

const sliderSettingsByGroup = {
  [GROUPS.CASINO]: index => casinoSliderSettings[index] || defaultCasinoSliderSettings,
  [GROUPS.LIVE]: () => liveCasinoSliderSettings,
};

const placeholderStyles = { height: 192 };
const CasinoCategoryPlaceholder = React.forwardRef((props, ref) => {
  return <div className={props.className} ref={ref} style={placeholderStyles} />;
});

CasinoCategoryPlaceholder.propTypes = {
  className: PropTypes.string,
};

const HomePageGames = ({ isFullWidth, setActiveItem }) => {
  const [initialRender, setInitialRender] = useState(true);
  const { group, identifyFavoriteGames } = useCasinoNewState();
  const { updateFilter } = useCasinoFilterState();

  const { authenticated } = useApplicationState();
  const { categoryGames, loading } = useCategoryGamesState();

  const getFilter = filterType => {
    const { search } = history.location;
    const query = new URLSearchParams(search);

    return query.get(filterType);
  };

  const searchQuery = getFilter(casinoFilterTypeMap.name);

  const sortedCategoriesWithGames = useMemo(
    () => categoryGames.sort((a, b) => a.position - b.position),
    [categoryGames],
  );

  const categoriesWithGames = useMemo(() => {
    const search = searchQuery || '';

    const newArr = sortedCategoriesWithGames.map(el => ({
      ...el,
      games: el.games.filter(game => game.name.toLowerCase().includes(search.toLowerCase())),
    }));

    return newArr.filter(el => el.games.length);
  }, [sortedCategoriesWithGames, searchQuery]);

  useEffect(() => {
    if (!authenticated || isEmptyCollection(categoryGames)) return;

    const gameIds = categoryGames.reduce((acc, { games }) => {
      const ids = games.map(({ id }) => id);

      return [...acc, ...ids];
    }, []);

    identifyFavoriteGames(gameIds);
  }, [categoryGames, authenticated, identifyFavoriteGames]);

  const getSliderSettings = useCallback(index => sliderSettingsByGroup[group](index), [group]);

  const showCategoryHandler = useCallback(
    categoryId => () => {
      updateFilter({ providerNames: [] });
      deleteCasinoFilter(casinoFilterTypeMap.providerNames);
      setActiveItem(categoryId.toString());
      scrollToTopHandler('auto');
    },
    [setActiveItem, updateFilter],
  );

  useEffect(() => {
    setInitialRender(false);
  }, []);

  if (loading || initialRender) return <Loader />;

  return categoriesWithGames.length ? (
    <div>
      {categoriesWithGames.map((category, index) => (
        <ViewportDisplayController
          className="casino__category placeholder"
          key={category.id}
          placeholder={CasinoCategoryPlaceholder}
        >
          <CasinoCategory
            category={category}
            isFullWidth={isFullWidth}
            showCategoryHandler={showCategoryHandler}
            sliderSettings={getSliderSettings(index)}
          />
        </ViewportDisplayController>
      ))}
    </div>
  ) : (
    <EmptyState />
  );
};

HomePageGames.propTypes = {
  isFullWidth: PropTypes.bool,
  setActiveItem: PropTypes.func,
};

export default HomePageGames;
